import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { EVENTS, IMG } from "../data/site";

const SPRIZZ = [
  { to: "/concept", label: "Il concept" },
  { to: "/cocktail-e-servizi", label: "Cocktail e servizi" },
  { to: "/allestimenti", label: "Allestimenti" },
  { to: "/gallery", label: "Gallery" },
];
const PRIVATI = EVENTS.filter((e) => e.slug !== "eventi-pubblici");
const MAIN = [{ to: "/partner", label: "Partner" }];

const linkCls = ({ isActive }) =>
  `text-[11px] md:text-xs font-medium uppercase tracking-[0.18em] transition-colors duration-200 ${
    isActive ? "text-brand-orange" : "text-white/70 hover:text-white"
  }`;

function NavDrop({ id, label, items, drop, setDrop, itemTest }) {
  const open = drop === id;
  return (
    <div className="relative" onMouseEnter={() => setDrop(id)} onMouseLeave={() => setDrop(null)}>
      <button
        data-testid={`nav-dropdown-btn-${id}`}
        onClick={() => setDrop(open ? null : id)}
        className="flex items-center gap-1.5 text-[11px] md:text-xs font-medium uppercase tracking-[0.18em] text-white/70 hover:text-white transition-colors duration-200"
      >
        {label}
        <ChevronDown
          size={13}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-64"
          >
            <div className="rounded-2xl border border-white/10 bg-brand-ink/95 backdrop-blur-xl p-2 shadow-2xl shadow-black/60">
              {items.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={itemTest(l)}
                  className="group flex items-center justify-between gap-2 rounded-xl px-4 py-3 text-sm text-white/75 hover:text-brand-orange hover:bg-white/[0.04] transition-colors duration-200"
                >
                  {l.label}
                  <ArrowUpRight
                    size={14}
                    className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
                  />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(null);
  const { pathname } = useLocation();
  const isPubblici = pathname === "/eventi-pubblici";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDrop(null);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled || open
            ? "bg-brand-ink/80 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        } ${!scrolled && !open && pathname === "/wedding" ? "navbar-light-top" : ""}`}
      >
        <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center shrink-0 rounded-xl bg-brand-ink px-2.5 py-1.5">
            <img
              src={IMG.logo}
              alt="Sprizziamoci — L'aperitivo on the road"
              className="h-9 md:h-11 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            <NavDrop
              id="sprizziamoci"
              label="Sprizziamoci"
              items={SPRIZZ}
              drop={drop}
              setDrop={setDrop}
              itemTest={(l) => `nav-dropdown-item-${l.to}`}
            />
            <NavDrop
              id="eventi-privati"
              label="Eventi privati"
              items={PRIVATI.map((e) => ({ to: e.path, label: e.label, tid: `event-dropdown-item-${e.slug}` }))}
              drop={drop}
              setDrop={setDrop}
              itemTest={(l) => l.tid}
            />

            <NavLink to="/eventi-pubblici" data-testid="nav-link-/eventi-pubblici" className={linkCls}>
              Eventi pubblici
            </NavLink>

            {MAIN.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.to}`} className={linkCls}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to={isPubblici ? "/partner" : "/contatti"}
              data-testid="nav-cta-quote"
              className="hidden md:inline-flex rounded-full bg-brand-orange px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300"
            >
              {isPubblici ? "Raccontaci il tuo evento" : "Richiedi un preventivo"}
            </Link>
            <button
              data-testid="nav-open-btn"
              onClick={() => setOpen(true)}
              aria-label="Apri menu"
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
            >
              <Menu size={19} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-brand-ink/98 backdrop-blur-2xl flex flex-col"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
              <img src={IMG.logo} alt="Sprizziamoci" className="h-9 w-auto mix-blend-screen" />
              <button
                data-testid="nav-close-btn"
                onClick={() => setOpen(false)}
                aria-label="Chiudi menu"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
              >
                <X size={19} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-1">
              <Link to="/" data-testid="mobile-link-home" className="drawer-link font-display text-3xl font-semibold py-2">
                Home
              </Link>
              <Link
                to="/eventi-pubblici"
                data-testid="mobile-link-eventi-pubblici"
                className="drawer-link font-display text-3xl font-semibold py-2"
              >
                Eventi pubblici
              </Link>
              {MAIN.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-link-${l.to}`}
                  className="drawer-link font-display text-3xl font-semibold py-2"
                >
                  {l.label}
                </Link>
              ))}
              <p className="eyebrow pt-6 pb-2">Sprizziamoci</p>
              {SPRIZZ.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-link-${l.to}`}
                  className="drawer-link font-display text-2xl font-medium py-1.5 text-white/80"
                >
                  {l.label}
                </Link>
              ))}
              <p className="eyebrow pt-6 pb-2">Eventi privati</p>
              {PRIVATI.map((e) => (
                <Link
                  key={e.slug}
                  to={e.path}
                  data-testid={`mobile-link-${e.slug}`}
                  className="drawer-link font-display text-2xl font-medium py-1.5 text-white/80"
                >
                  {e.label}
                </Link>
              ))}
              <Link to="/contatti" data-testid="mobile-link-contatti" className="mt-6">
                <span className="inline-flex w-full items-center justify-center rounded-full bg-brand-orange px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink">
                  Contatti e preventivi
                </span>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
